import React from 'react';
import {
  MARKDOWN_TEMPLATE_HYPOTHEEK_ADVISORS,
  MARKDOWN_TEMPLATE_MAKELAARS,
  TEXT,
} from '../../constants';
import JustificationContainer from '~/components/atom/JustificationContainer';
import LfaMarkdown from '~/components/atom/LfaMarkdown';
import { Heading4, Label, Variant } from '~/components/atom/Typography';
import SectionContainer from '../SectionContainer';
import styled, { css } from 'styled-components';
import GridContainer from '~/components/atom/GridContainer';
import Textarea from '~/components/molecule/Textarea';
import type {
  UpdateSettingsAction,
  UpdateSettingsState,
} from '../../reducers/settingsReducer';
import useTooltipLayer from '~/hooks/useTooltipLayer';
import Button from '~/components/atom/Button';

type Props = {
  updateSettings: UpdateSettingsState;
  dispatch: React.Dispatch<UpdateSettingsAction>;
};

const AccountDescriptionSection: React.FCC<Props> = ({
  updateSettings,
  dispatch,
}) => {
  const tooltipPropsMakelaars = useTooltipLayer({
    tooltipMessage: TEXT.markdownTemplateMakelaarsTooltip,
  });
  const tooltipPropsHypotheekAdvisors = useTooltipLayer({
    tooltipMessage: TEXT.markdownTemplateHypotheekAdvisorsTooltip,
  });

  return (
    <SectionContainer>
      <Heading4 size="m">{TEXT.accountDescriptionHeader}</Heading4>

      <DescriptionContainer
        width="100%"
        backgroundColor={{ group: 'white', variant: 'dark' }}
        border={{
          radius: 'base',
          width: 's',
          color: { group: 'tertiary', variant: 'light' },
        }}
        columns="repeat(2, 1fr)"
        gap="base"
        padding={['s']}
      >
        <StyledTextarea
          height="100%"
          name="description"
          value={updateSettings?.description ?? ''}
          onChange={value => {
            dispatch({ type: 'SET_DESCRIPTION', payload: value ?? '' });
          }}
          label={
            <JustificationContainer align="center" gap="xxs">
              {TEXT.markdownEditorLabel}
              <StyledButton
                appearance="tertiary"
                onClick={() => {
                  dispatch({
                    type: 'SET_DESCRIPTION',
                    payload: MARKDOWN_TEMPLATE_MAKELAARS,
                  });
                }}
                label={TEXT.makelaars}
                {...tooltipPropsMakelaars}
              />
              <StyledButton
                appearance="tertiary"
                onClick={() => {
                  dispatch({
                    type: 'SET_DESCRIPTION',
                    payload: MARKDOWN_TEMPLATE_HYPOTHEEK_ADVISORS,
                  });
                }}
                {...tooltipPropsHypotheekAdvisors}
                label={TEXT.hypotheekAdvisors}
              />
            </JustificationContainer>
          }
        />
        <JustificationContainer direction="column" gap="xxxs" width="100%">
          <Label size="base" margin={[null]} variant={Variant.primary}>
            {TEXT.preview}
          </Label>
          <PreviewContainer
            border={{
              radius: 'base',
              width: 's',
              color: { group: 'tertiary' },
            }}
            gap="base"
            padding={['s']}
            backgroundColor={{ group: 'white' }}
            height="100%"
            width="100%"
          >
            <LfaMarkdown value={updateSettings?.description ?? ''} />
          </PreviewContainer>
        </JustificationContainer>
      </DescriptionContainer>
    </SectionContainer>
  );
};

const CONTAINER_HEIGHT = '300px';
const StyledTextarea = styled(Textarea)`
  min-height: ${CONTAINER_HEIGHT};
  height: 100%;
`;

const PreviewContainer = styled(JustificationContainer)`
  min-height: ${CONTAINER_HEIGHT};
`;

const DescriptionContainer = styled(GridContainer)`
  overflow-y: auto;
`;

const StyledButton = styled(Button)(
  ({ theme }) => css`
    padding: ${theme.space('xxxs')} ${theme.space('xxs')};
  `,
);

export default AccountDescriptionSection;
